import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ArrowUpRight } from "lucide-react";
import imgEnergy from "@/assets/chlm-live-tea.jpg";
import imgSkinAsset from "@/assets/marapa-glow-ritual-skincare-trio.png.asset.json";
import imgHairAsset from "@/assets/grow-healthy-hair-yaphene.jpg.asset.json";
import imgEmotional from "@/assets/anaelli-reflections-writing.jpg";
import imgComeBack from "@/assets/forest/rooted-ritual-hero.jpg";
import imgLongevity from "@/assets/chlm-botanicals.jpg";
import imgIsland from "@/assets/experience-st-kitts-sunset.jpg";
import imgHerbal from "@/assets/chlm-step-herbal-immersion.jpg";

const imgHair = imgHairAsset.url;
const imgSkin = imgSkinAsset.url;

type Pathway = {
  number: string;
  title: string;
  focus: string;
  image: string;
  to: string;
  objectPosition?: string;
};

const PATHWAYS: Pathway[] = [
  { number: "01", title: "Restore My Energy", focus: "Tea rituals · vitality foods · reset planner", image: imgEnergy, to: "/pathways/restore-your-rhythm" },
  { number: "02", title: "Heal My Skin", focus: "Marapa · botanical oils · glow rituals", image: imgSkin, to: "/pathways/glow-through-ritual" },
  { number: "03", title: "Grow Healthy Hair", focus: "Yaphene rituals · scalp wellness · wash day traditions", image: imgHair, to: "/pathways/island-hair-ritual", objectPosition: "50% 38%" },
  { number: "04", title: "Emotional Wellness", focus: "Journals · reflection rituals · nervous system calm", image: imgEmotional, to: "/pathways/return-to-yourself" },
  { number: "05", title: "Come Back to Yourself", focus: "Guided rituals · music · reflection · journaling", image: imgComeBack, to: "/pathways/come-back-to-yourself" },
  { number: "06", title: "Caribbean Longevity", focus: "Bush teas · longevity plate · herbal cabinet", image: imgLongevity, to: "/caribbean-heritage-longevity-method" },
  { number: "07", title: "Experience St. Kitts", focus: "Discover · retreats · Balm & Bond · botanical walks", image: imgIsland, to: "/pathways/island-behind-method" },
  { number: "08", title: "Learn Herbal Traditions", focus: "Guides · botanical index · courses · workshops", image: imgHerbal, to: "/island-library" },
];

const BeginYourJourney = () => {
  return (
    <section id="begin-journey" className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-6" style={{ maxWidth: "1400px" }}>
        <Reveal className="max-w-2xl mb-16 md:mb-20">
          <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-5">
            Pathways
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[3.5rem] text-foreground leading-[1.05] mb-6">
            Begin Your Journey
          </h2>
          <p className="font-sans text-base md:text-lg text-foreground/70 leading-relaxed">
            Choose a pathway shaped by your rhythms, wellness goals and the
            traditions of Caribbean living.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 font-sans text-[11px] tracking-[0.28em] uppercase">
            <Link
              to="/rhythm"
              className="inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors border-b border-accent/50 pb-1"
            >
              Discover Your Caribbean Wellness Rhythm
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/quiz"
              className="inline-flex items-center gap-2 text-foreground/55 hover:text-accent transition-colors"
            >
              Hair &amp; Skin Diagnostic
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {PATHWAYS.map((p, idx) => (
            <Reveal key={p.title} delay={idx * 60}>
              <Link
                to={p.to}
                className="group relative block overflow-hidden rounded-sm aspect-[4/5] bg-[hsl(150_35%_8%)]"
              >
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  style={p.objectPosition ? { objectPosition: p.objectPosition } : undefined}
                  className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-70 group-hover:scale-[1.04] transition-all duration-[900ms] ease-out"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, hsl(150 35% 8% / 0.15) 0%, hsl(150 35% 8% / 0.35) 50%, hsl(150 35% 8% / 0.92) 100%)",
                  }}
                />
                <div className="absolute inset-0 flex flex-col justify-between p-7 md:p-8">
                  <div className="flex items-start justify-between">
                    <span className="font-serif italic text-accent/90 text-sm tracking-wide">
                      {p.number}
                    </span>
                    <ArrowUpRight className="w-5 h-5 text-[hsl(40_33%_94%)]/70 group-hover:text-accent group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-500" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl md:text-[1.7rem] text-[hsl(40_33%_96%)] leading-tight mb-3 group-hover:text-accent transition-colors duration-500">
                      {p.title}
                    </h3>
                    <p className="font-sans text-[13px] text-[hsl(40_33%_94%)]/75 leading-relaxed">
                      {p.focus}
                    </p>
                    <span className="mt-5 inline-block h-px w-10 bg-accent/70 group-hover:w-24 transition-all duration-500" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BeginYourJourney;
